import { CUSTOMIZATION_NAMES } from '../..';
import { addTranslation } from '../translations';
import {
  Customization,
  CustomizationKey,
  CustomizationColors,
  CustomizationActions,
  CustomizationAssets,
  CustomizationParams,
} from './customization.model';

const { REACT_APP_CUSTOMIZATION_BASE_URL: CUSTOMIZATION_BASE_URL } = process.env;
if (!CUSTOMIZATION_BASE_URL) throw new Error('Config REACT_APP_CUSTOMIZATION_BASE_URL not set');

const getBaseClientUrl = (customizationKey: CustomizationKey): string =>
  `${CUSTOMIZATION_BASE_URL}${customizationKey.customizationId}/${CUSTOMIZATION_NAMES.CUSTOMIZATION_COMMON_FOLDER}/`;
const getBaseProjectUrl = (customizationKey: CustomizationKey): string =>
  `${CUSTOMIZATION_BASE_URL}${customizationKey.customizationId}/${customizationKey.projectId}/${CUSTOMIZATION_NAMES.CUSTOMIZATION_COMMON_FOLDER}/`;
const getBaseProductUrl = (customizationKey: CustomizationKey): string =>
  `${CUSTOMIZATION_BASE_URL}${customizationKey.customizationId}/${customizationKey.projectId}/${customizationKey.productId}/`;

const loadCustomization = (customizationUrl: string): Promise<Customization | null> => {
  return fetch(customizationUrl)
    .then((res) => res.json())
    .catch(() => {
      return null;
    });
};

const renderStyle = (element: HTMLElement, colors: CustomizationColors | null) => {
  if (colors && Object.keys(colors).length) {
    Object.entries(colors).forEach(([key, value]) => element.style.setProperty(`--${key}`, value));
  }
};

const overrideTranslations = (
  clientCustomization: Customization | null,
  projectCustomization: Customization | null,
  productCustomization: Customization | null
) => {
  if (clientCustomization?.translations) {
    for (const [language, resources] of Object.entries(clientCustomization.translations)) {
      for (const [key, value] of Object.entries(resources)) {
        addTranslation(language, key, value);
      }
    }
  }
  if (projectCustomization?.translations) {
    for (const [language, resources] of Object.entries(projectCustomization.translations)) {
      for (const [key, value] of Object.entries(resources)) {
        addTranslation(language, key, value);
      }
    }
  }

  if (productCustomization?.translations) {
    for (const [language, resources] of Object.entries(productCustomization.translations)) {
      for (const [key, value] of Object.entries(resources)) {
        addTranslation(language, key, value);
      }
    }
  }
};

const overrideAssets = (
  clientCustomization: Customization | null,
  projectCustomization: Customization | null,
  productCustomization: Customization | null,
  customizationKey: CustomizationKey
) => {
  let assets: CustomizationAssets | null = null;
  if (clientCustomization?.assets) {
    assets = Object.entries(clientCustomization.assets).reduce(
      (acc, [key, value]) => ({
        ...acc,
        [key]: Array.isArray(value)
          ? value.map((value) => `${getBaseClientUrl(customizationKey)}${value}`)
          : `${getBaseClientUrl(customizationKey)}${value}`,
      }),
      {}
    );
  }

  if (!!projectCustomization && projectCustomization?.assets) {
    const projectAssets = Object.entries(projectCustomization.assets).reduce(
      (acc, [key, value]) => ({
        ...acc,
        [key]: `${getBaseProjectUrl(customizationKey)}${value}`,
      }),
      {}
    );
    if (!!projectAssets && !!assets) Object.assign(assets, projectAssets);
  }

  if (!!productCustomization && productCustomization?.assets) {
    const productAssets = Object.entries(productCustomization.assets).reduce(
      (acc, [key, value]) => ({
        ...acc,
        [key]: `${getBaseProductUrl(customizationKey)}${value}`,
      }),
      {}
    );
    if (!!productAssets && !!assets) Object.assign(assets, productAssets);
  }

  return assets;
};

const overrideParams = (
  clientCustomization: Customization | null,
  projectCustomization: Customization | null,
  productCustomization: Customization | null
) => {
  let params: CustomizationParams | null = null;
  if (clientCustomization?.params) params = clientCustomization?.params;
  if (!!projectCustomization && projectCustomization?.params && !!params) Object.assign(params, projectCustomization?.params);
  if (!!productCustomization && productCustomization?.params && !!params) Object.assign(params, productCustomization?.params);
  return params;
};

const load = async (customizationKey: CustomizationKey): Promise<CustomizationActions> => {
  const clientCustomization: Customization | null = await loadCustomization(
    `${getBaseClientUrl(customizationKey)}${CUSTOMIZATION_NAMES.CUSTOMIZATION_FILE_NAME}`
  );
  const projectCustomization: Customization | null = !!customizationKey.projectId
    ? await loadCustomization(`${getBaseProjectUrl(customizationKey)}${CUSTOMIZATION_NAMES.CUSTOMIZATION_FILE_NAME}`)
    : null;

  const productCustomization: Customization | null = !!customizationKey.projectId
    ? await loadCustomization(`${getBaseProductUrl(customizationKey)}${CUSTOMIZATION_NAMES.CUSTOMIZATION_FILE_NAME}`)
    : null;

  const isCustomization = (obj: Customization | null): obj is Customization => !!clientCustomization;
  const assets: CustomizationAssets | null = overrideAssets(clientCustomization, projectCustomization, productCustomization, customizationKey);
  const params: CustomizationParams | null = overrideParams(clientCustomization, projectCustomization, productCustomization);

  return {
    isCustomization: () => isCustomization(clientCustomization),
    renderStyle: (element: HTMLElement): void => {
      clientCustomization?.colors && renderStyle(element, clientCustomization.colors);
    },
    setTranslation: (): void => {
      overrideTranslations(clientCustomization, projectCustomization, productCustomization);
    },
    getAssets: (): CustomizationAssets | null => {
      return assets;
    },
    getParams: (): CustomizationParams | null => {
      return params || null;
    },
  };
};

export type CustomizationService = ReturnType<typeof load>;

export { load };
